import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Download, FileText, Calendar, Filter, FileDown } from 'lucide-react';
import { Button } from './ui/button';
import { AnimateIn } from './Layout';
import axios from 'axios';
import { format, subDays, parseISO } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0B1F3A', '#C9A646', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'];

const AnalyticsDashboard = ({ authToken }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, startDate, endDate]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      let params = '';
      if (dateRange === 'custom' && startDate && endDate) {
        params = `?start_date=${startDate}&end_date=${endDate}`;
      } else if (dateRange === '7days') {
        const start = format(subDays(new Date(), 7), 'yyyy-MM-dd');
        params = `?start_date=${start}`;
      } else if (dateRange === '30days') {
        const start = format(subDays(new Date(), 30), 'yyyy-MM-dd');
        params = `?start_date=${start}`;
      } else if (dateRange === '90days') {
        const start = format(subDays(new Date(), 90), 'yyyy-MM-dd');
        params = `?start_date=${start}`;
      }

      const res = await axios.get(`${API}/admin/analytics${params}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics', err);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    if (!analytics) return;
    
    // Create CSV content
    let csv = 'Analytics Report\n\n';
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;
    
    csv += 'OVERVIEW\n';
    csv += `Total Resources,${analytics.overview.total_resources}\n`;
    csv += `Total Downloads,${analytics.overview.total_downloads}\n`;
    csv += `Avg Downloads per Resource,${analytics.overview.avg_downloads_per_resource}\n\n`;
    
    csv += 'RESOURCES BY CATEGORY\n';
    csv += 'Category,Count\n';
    Object.entries(analytics.resources_by_category).forEach(([cat, count]) => {
      csv += `${cat},${count}\n`;
    });
    
    csv += '\nRESOURCES BY STATE\n';
    csv += 'State,Count\n';
    Object.entries(analytics.resources_by_state).forEach(([state, count]) => {
      csv += `${state},${count}\n`;
    });
    
    csv += '\nDOWNLOADS BY STATE\n';
    csv += 'State,Downloads\n';
    Object.entries(analytics.downloads_by_state).forEach(([state, count]) => {
      csv += `${state},${count}\n`;
    });
    
    csv += '\nTOP DOWNLOADED RESOURCES\n';
    csv += 'Title,Downloads\n';
    analytics.top_downloaded.forEach(item => {
      csv += `"${item.title}",${item.downloads}\n`;
    });
    
    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="w-8 h-8 border-2 border-[#C9A646] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-400 font-body">Loading analytics...</p>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-400 font-body">No analytics data available</p>
      </div>
    );
  }

  // Prepare chart data
  const categoryData = Object.entries(analytics.resources_by_category).map(([name, value]) => ({
    name,
    value
  }));

  const stateData = Object.entries(analytics.resources_by_state).slice(0, 10).map(([name, count]) => ({
    name: name.length > 12 ? name.substring(0, 12) + '...' : name,
    fullName: name,
    count
  }));

  const uploadTrendData = Object.entries(analytics.upload_trends)
    .filter(([date]) => date && date.length > 0)
    .map(([date, count]) => ({
      date: format(parseISO(date + 'T00:00:00'), 'MMM dd'),
      uploads: count
    }));

  const downloadTrendData = Object.entries(analytics.download_trends)
    .filter(([date]) => date && date.length > 0)
    .map(([date, count]) => ({
      date: format(parseISO(date + 'T00:00:00'), 'MMM dd'),
      downloads: count
    }));

  const downloadsByStateData = Object.entries(analytics.downloads_by_state).slice(0, 10).map(([name, count]) => ({
    name: name.length > 12 ? name.substring(0, 12) + '...' : name,
    fullName: name,
    downloads: count
  }));

  return (
    <div className="space-y-8">
      {/* Filters & Export */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-400" />
            <span className="text-sm font-semibold text-gray-600">Date Range:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setDateRange('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dateRange === 'all'
                  ? 'bg-[#0B1F3A] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All Time
            </button>
            <button
              onClick={() => setDateRange('7days')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dateRange === '7days'
                  ? 'bg-[#0B1F3A] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Last 7 Days
            </button>
            <button
              onClick={() => setDateRange('30days')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dateRange === '30days'
                  ? 'bg-[#0B1F3A] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Last 30 Days
            </button>
            <button
              onClick={() => setDateRange('90days')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dateRange === '90days'
                  ? 'bg-[#0B1F3A] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Last 90 Days
            </button>
          </div>
        </div>
        
        {dateRange === 'custom' ? (
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
        ) : (
          <button
            onClick={() => setDateRange('custom')}
            className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
          >
            <Calendar size={16} className="inline mr-1.5" />
            Custom Range
          </button>
        )}

        <Button onClick={exportToExcel} variant="outline" className="border-[#C9A646] text-[#C9A646] hover:bg-[#C9A646] hover:text-white">
          <FileDown size={16} className="mr-2" />
          Export to Excel
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <AnimateIn delay={0}>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-lg shadow-lg text-white">
            <div className="flex items-center justify-between mb-2">
              <FileText size={24} className="opacity-80" />
              <TrendingUp size={20} className="opacity-60" />
            </div>
            <h3 className="text-3xl font-bold mb-1">{analytics.overview.total_resources}</h3>
            <p className="text-sm opacity-90">Total Resources</p>
          </div>
        </AnimateIn>

        <AnimateIn delay={100}>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-lg shadow-lg text-white">
            <div className="flex items-center justify-between mb-2">
              <Download size={24} className="opacity-80" />
              <TrendingUp size={20} className="opacity-60" />
            </div>
            <h3 className="text-3xl font-bold mb-1">{analytics.overview.total_downloads}</h3>
            <p className="text-sm opacity-90">Total Downloads</p>
          </div>
        </AnimateIn>

        <AnimateIn delay={200}>
          <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-lg shadow-lg text-white">
            <div className="flex items-center justify-between mb-2">
              <FileText size={24} className="opacity-80" />
              <Calendar size={20} className="opacity-60" />
            </div>
            <h3 className="text-3xl font-bold mb-1">{analytics.overview.resources_in_period}</h3>
            <p className="text-sm opacity-90">Resources in Period</p>
          </div>
        </AnimateIn>

        <AnimateIn delay={300}>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-lg shadow-lg text-white">
            <div className="flex items-center justify-between mb-2">
              <Download size={24} className="opacity-80" />
              <FileText size={20} className="opacity-60" />
            </div>
            <h3 className="text-3xl font-bold mb-1">{analytics.overview.avg_downloads_per_resource}</h3>
            <p className="text-sm opacity-90">Avg Downloads/Resource</p>
          </div>
        </AnimateIn>
      </div>

      {/* Charts Row 1: Category & State Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AnimateIn delay={400}>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <h3 className="text-lg font-bold text-[#0B1F3A] mb-4">Resources by Category</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </AnimateIn>

        <AnimateIn delay={500}>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <h3 className="text-lg font-bold text-[#0B1F3A] mb-4">Top States by Resources</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stateData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
                        <p className="font-semibold">{payload[0].payload.fullName}</p>
                        <p className="text-sm text-gray-600">Resources: {payload[0].value}</p>
                      </div>
                    );
                  }
                  return null;
                }} />
                <Bar dataKey="count" fill="#0B1F3A" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </AnimateIn>
      </div>

      {/* Charts Row 2: Trends */}
      {uploadTrendData.length > 0 && (
        <AnimateIn delay={600}>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <h3 className="text-lg font-bold text-[#0B1F3A] mb-4">Upload Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={uploadTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="uploads" stroke="#3B82F6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </AnimateIn>
      )}

      {/* Charts Row 3: Download Analytics */}
      {analytics.overview.total_downloads > 0 && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <AnimateIn delay={700}>
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <h3 className="text-lg font-bold text-[#0B1F3A] mb-4">Downloads by State</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={downloadsByStateData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
                            <p className="font-semibold">{payload[0].payload.fullName}</p>
                            <p className="text-sm text-gray-600">Downloads: {payload[0].value}</p>
                          </div>
                        );
                      }
                      return null;
                    }} />
                    <Bar dataKey="downloads" fill="#C9A646" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </AnimateIn>

            <AnimateIn delay={800}>
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <h3 className="text-lg font-bold text-[#0B1F3A] mb-4">Top Downloaded Resources</h3>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {analytics.top_downloaded.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-[#0B1F3A] truncate">{item.title}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <Download size={14} className="text-gray-400" />
                        <span className="font-bold text-[#C9A646]">{item.downloads}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimateIn>
          </div>

          {downloadTrendData.length > 0 && (
            <AnimateIn delay={900}>
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <h3 className="text-lg font-bold text-[#0B1F3A] mb-4">Download Trends</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={downloadTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="downloads" stroke="#10B981" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </AnimateIn>
          )}
        </>
      )}

      {/* Recent Activity */}
      <AnimateIn delay={1000}>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-lg font-bold text-[#0B1F3A] mb-4">Recent Uploads</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">Title</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">State</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">Category</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">Date</th>
                </tr>
              </thead>
              <tbody>
                {analytics.recent_uploads.map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">{item.title}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{item.state}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{item.category}</td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {item.created_at ? format(parseISO(item.created_at), 'MMM dd, yyyy') : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AnimateIn>
    </div>
  );
};

export default AnalyticsDashboard;
